import type {Props as ResultItemProps} from '@/modules/common/components/FilterSearch/components/ResultItem';
import type {Item} from '@/modules/common/components/FilterSearch/utils/getFiltersForSearch';

import {css} from '@emotion/react';
import {useBreakpointDown} from '@travel-frontend/core';
import {IconCancel, IconSearch} from '@travel-frontend/icons';
import {
  Flex,
  LabelSmall,
  SPACING_NONE,
  Stack,
  TextField,
} from '@travel-frontend/ui-components';
import {useCallback, useMemo, useRef, useState} from 'react';
import {isMobile} from 'react-device-detect';
import {useTranslation} from 'react-i18next';

import {INVIA_TYPE_DISCOUNT} from '@/consts';
import {ResultItem} from '@/modules/common/components/FilterSearch/components/ResultItem';
import {useTourOperatorDiscountCampaign} from '@/modules/common/hooks';
import {getScrollingParentElement} from '@/utils/getScrollingParentElement';

type Props = {
  onChange: ResultItemProps['onChange'];
  items: Array<Item>;
};

export const FilterSearch = ({items, onChange}: Props) => {
  const {t} = useTranslation(['filterSearch']);
  const [searchTerm, setSearchTerm] = useState('');
  const filterSearchContainerRef = useRef<HTMLDivElement>(null);
  const mdDown = useBreakpointDown('md');

  const {shouldShowFilter, filterInterceptorContent} =
    useTourOperatorDiscountCampaign();

  const searchResults = useMemo(() => {
    const userInputLower = searchTerm
      .toLocaleLowerCase(import.meta.env.VITE_LOCALE)
      .trim();

    return items.filter((item) => {
      if (userInputLower.length < 2) {
        return false;
      }

      // edge case so that user can filter by campign filter name but also the original wording
      // eg: filterInterceptorContent.text=HOLIDAY - filterInterceptorContent.hightlight=DEAL - item.label=Veranstalter-Rabatte
      if (item.value === INVIA_TYPE_DISCOUNT && shouldShowFilter) {
        return `${item.label} ${filterInterceptorContent.text} ${filterInterceptorContent.highlight}`
          .toLocaleLowerCase(import.meta.env.VITE_LOCALE)
          .includes(userInputLower);
      }

      return item.label
        .toLocaleLowerCase(import.meta.env.VITE_LOCALE)
        .includes(userInputLower);
    });
  }, [
    filterInterceptorContent.highlight,
    filterInterceptorContent.text,
    items,
    searchTerm,
    shouldShowFilter,
  ]);

  /**
   * onMouseDown on smaller screens
   * (actually onFocus but we use onMouseDown to come by mobile scroll behaviors)
   * we want to scroll the input to the top inside the overlay
   * so that the user can see more results directly
   */
  const handleScrollToTop: React.MouseEventHandler<HTMLInputElement> =
    useCallback(
      (event) => {
        // when the input already has focus we don't want to scroll
        if (event.currentTarget.parentElement?.querySelector('input:focus')) {
          return;
        }

        if (!mdDown || !filterSearchContainerRef.current) {
          return;
        }

        const scrollParent = getScrollingParentElement(
          filterSearchContainerRef.current
        );

        if (scrollParent) {
          scrollParent.scrollTo({
            top:
              filterSearchContainerRef.current.offsetTop -
              scrollParent.offsetTop -
              // static margin to make it look nicer
              16,
            // "instant" on mobile to prevent side effects of multiple scrolls with animation at the same time
            behavior: isMobile ? 'instant' : 'smooth',
          });
        }
      },
      [mdDown]
    );

  const hasResults = searchResults.length > 0;
  const hasNoResultsButUserInput = searchTerm.length > 1 && !hasResults;

  return (
    <Flex ref={filterSearchContainerRef}>
      <TextField
        css={css`
          label {
            /* @todo should be done correctly in toolkit */
            max-width: calc(100% - 50px);
          }
        `}
        value={searchTerm}
        onChange={(event) => {
          setSearchTerm(event.target.value);
        }}
        prependIcon={<IconSearch />}
        aria-label={t('inputLabel')}
        placeholder={t('inputLabel')}
        appendIcon={
          searchTerm && (
            <IconCancel
              data-testid="shared-filtersearch-reset"
              onClick={() => setSearchTerm('')}
              css={(theme) => css`
                cursor: pointer;
                color: ${theme.colors.contentTertiary};
              `}
            />
          )
        }
        onMouseDown={handleScrollToTop}
      />
      {hasNoResultsButUserInput && <LabelSmall>{t('noResults')}</LabelSmall>}
      {hasResults && (
        <Stack direction="column" spacing={SPACING_NONE}>
          {searchResults.map((item) => (
            <ResultItem
              key={item.label + item.filter.id}
              item={item}
              onChange={onChange}
              searchTerm={searchTerm}
            />
          ))}
        </Stack>
      )}
    </Flex>
  );
};
